import { useEffect } from "react";
import { DEFAULT_OG_IMAGE, SITE_URL } from "@/lib/site";

export interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: string;
  noindex?: boolean;
  /** Absolute URL of the LCP image for this route; injected as <link rel="preload" as="image">. */
  preloadImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  breadcrumbs?: BreadcrumbItem[];
}

function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export default function SEOHead({
  title,
  description,
  path,
  keywords,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt,
  ogType = "website",
  noindex = false,
  preloadImage,
  jsonLd,
  breadcrumbs,
}: SEOHeadProps) {
  useEffect(() => {
    const suffix = " | RTW Photography";
    const candidate = title.includes("RTW Photography") ? title : `${title}${suffix}`;
    // Keep titles under ~60 chars for SERP display; drop the suffix if it pushes us over.
    let fullTitle = candidate.length > 60 && !title.includes("RTW Photography") ? title : candidate;
    // Hard cap at 70 chars — Google truncates around 60 in the SERP, and HubSpot
    // flags anything over 70 as SHORTEN_TITLE. Trim at a word boundary and append
    // an ellipsis so long DB-authored blog titles don't render as raw truncation.
    if (fullTitle.length > 70) {
      const slice = fullTitle.slice(0, 67);
      const lastSpace = slice.lastIndexOf(" ");
      fullTitle = `${(lastSpace > 40 ? slice.slice(0, lastSpace) : slice).replace(/[.,;:!?-]+$/, "")}…`;
    }
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Google typically truncates snippets around 155–160 chars. Guard against long
    // post excerpts by trimming at a word boundary before appending an ellipsis.
    const trimmedDesc = (() => {
      const clean = description.trim().replace(/\s+/g, " ");
      if (clean.length <= 160) return clean;
      const slice = clean.slice(0, 157);
      const lastSpace = slice.lastIndexOf(" ");
      return `${(lastSpace > 120 ? slice.slice(0, lastSpace) : slice).replace(/[.,;:!?-]+$/, "")}…`;
    })();
    setMeta("description", trimmedDesc);
    if (keywords) setMeta("keywords", keywords);
    if (noindex) {
      setMeta("robots", "noindex, nofollow");
    } else {
      document.querySelector('meta[name="robots"]')?.remove();
    }

    const canonicalUrl = `${SITE_URL}${path}`;
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // Social crawlers require absolute https URLs. Page-level images are often
    // app-relative (/__l5e/... asset paths), so resolve them against SITE_URL and
    // keep og:image:secure_url in sync with the static fallback in index.html.
    const absoluteOgImage = /^https?:\/\//i.test(ogImage)
      ? ogImage
      : `${SITE_URL}${ogImage.startsWith("/") ? "" : "/"}${ogImage}`;

    setMeta("og:title", fullTitle, true);
    setMeta("og:description", trimmedDesc, true);
    setMeta("og:url", canonicalUrl, true);
    setMeta("og:type", ogType, true);
    setMeta("og:image", absoluteOgImage, true);
    setMeta("og:image:secure_url", absoluteOgImage, true);
    setMeta("og:image:alt", ogImageAlt || "RTW Photography", true);
    setMeta("og:site_name", "RTW Photography", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", trimmedDesc);
    setMeta("twitter:image", absoluteOgImage);
    setMeta("twitter:image:alt", ogImageAlt || "RTW Photography");


    document.querySelectorAll('script[data-seo-head="true"]').forEach((el) => el.remove());
    document.querySelectorAll('link[data-seo-preload="true"]').forEach((el) => el.remove());
    if (preloadImage) {
      const preload = document.createElement("link");
      preload.rel = "preload";
      preload.as = "image";
      preload.setAttribute("fetchpriority", "high");
      preload.setAttribute("data-seo-preload", "true");
      preload.href = preloadImage;
      document.head.appendChild(preload);
    }
    const allSchemas: Record<string, unknown>[] = [];
    if (breadcrumbs?.length) allSchemas.push(buildBreadcrumbSchema(breadcrumbs));
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      allSchemas.push(...arr);
    }
    for (const schema of allSchemas) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-head", "true");
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      document.querySelectorAll('script[data-seo-head="true"]').forEach((el) => el.remove());
      document.querySelectorAll('link[data-seo-preload="true"]').forEach((el) => el.remove());
    };
  }, [title, description, path, keywords, ogImage, ogImageAlt, ogType, noindex, preloadImage, jsonLd, breadcrumbs]);

  return null;
}